import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { useAuthStore } from '@/stores/auth';
import { useUIStore } from '@/stores/ui';
import { useAuth } from '@/hooks/useAuth';
import { useSessionTimeoutWarning } from '@/hooks/useSessionTimeoutWarning';
import { useSSESync } from '@/hooks/useSSESync';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { ToastRegion } from '@/components/ui/Toast';
import { CommandPalette } from '@/components/layout/CommandPalette';
import { GlobalQuickUploadModal } from '@/components/layout/GlobalQuickUploadModal';
import { LoginPage } from '@/pages/LoginPage';
import { SSOCallbackPage } from '@/pages/SSOCallbackPage';
import { AcceptInvitePage } from '@/pages/AcceptInvitePage';
import { PasswordResetRequestPage } from '@/pages/PasswordResetRequestPage';
import { PasswordResetConfirmPage } from '@/pages/PasswordResetConfirmPage';
import { ClientDashboardPage } from '@/pages/ClientDashboardPage';
import { StaffDashboardPage } from '@/pages/StaffDashboardPage';
import { WorkspacePage } from '@/pages/WorkspacePage';
import { WorkspaceTeamPage } from '@/pages/WorkspaceTeamPage';
import { WorkspaceActivityPage } from '@/pages/WorkspaceActivityPage';
import { FolderPage } from '@/pages/FolderPage';
import { FileDetailPage } from '@/pages/FileDetailPage';
import { RequestsPage } from '@/pages/RequestsPage';
import { RequestsKanbanPage } from '@/pages/RequestsKanbanPage';
import { RequestDetailPage } from '@/pages/RequestDetailPage';
import { MfaSetupRequiredPage } from '@/pages/MfaSetupRequiredPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { AdminLayout } from '@/pages/admin/AdminLayout';
import { OrganizationsPage } from '@/pages/admin/OrganizationsPage';
import { OrganizationDetailPage } from '@/pages/admin/OrganizationDetailPage';
import { UsersPage } from '@/pages/admin/UsersPage';
import { AuditLogPage } from '@/pages/admin/AuditLogPage';
import { FolderTemplatesPage } from '@/pages/admin/FolderTemplatesPage';
import { RequestTemplatesPage } from '@/pages/admin/RequestTemplatesPage';
import { SystemSettingsPage } from '@/pages/admin/SystemSettingsPage';
import { OnboardClientPage } from '@/pages/admin/OnboardClientPage';
import { SearchPage } from '@/pages/SearchPage';
import { ForbiddenPage, NotFoundPage } from '@/pages/NotFoundPage';
import { SessionExpiredPage } from '@/pages/SessionExpiredPage';
import { OrgPickerPage } from '@/pages/OrgPickerPage';
import { GuidedTour } from '@/components/layout/GuidedTour';

function RootRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'client_admin' || user.role === 'client_user') {
    return <Navigate to="/client" replace />;
  }
  return <Navigate to="/staff" replace />;
}

function AppInner() {
  const hydrate = useAuthStore((s) => s.hydrate);
  const activeUploads = useUIStore((s) => s.activeUploads);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  useSessionTimeoutWarning();

  const { user } = useAuth();
  useSSESync(!!user);
  usePushNotifications(!!user);

  useEffect(() => {
    if (activeUploads === 0) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [activeUploads]);

  return (
    <>
    <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/sso-callback" element={<SSOCallbackPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/password-reset" element={<PasswordResetRequestPage />} />
          <Route path="/password-reset/confirm" element={<PasswordResetConfirmPage />} />
          <Route path="/forbidden" element={<ForbiddenPage />} />
          <Route path="/session-expired" element={<SessionExpiredPage />} />
          <Route path="/mfa-setup-required" element={<MfaSetupRequiredPage />} />
          <Route path="/select-org" element={<OrgPickerPage />} />

          {/* Protected */}
          <Route path="/" element={<RootRedirect />} />
          <Route
            path="/client"
            element={
              <ProtectedRoute roles={['client_admin', 'client_user']}>
                <ClientDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/staff"
            element={
              <ProtectedRoute roles={['sysadmin', 'manager', 'staff']}>
                <StaffDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workspace/:orgId"
            element={
              <ProtectedRoute>
                <WorkspacePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workspace/:orgId/team"
            element={
              <ProtectedRoute>
                <WorkspaceTeamPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workspace/:orgId/activity"
            element={
              <ProtectedRoute>
                <WorkspaceActivityPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workspace/:orgId/folders/:folderId"
            element={
              <ProtectedRoute>
                <FolderPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/files/:fileId"
            element={
              <ProtectedRoute>
                <FileDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/requests"
            element={
              <ProtectedRoute>
                <RequestsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/requests/board"
            element={
              <ProtectedRoute>
                <RequestsKanbanPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/requests/:id"
            element={
              <ProtectedRoute>
                <RequestDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <SearchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          {/* Admin */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={['sysadmin', 'manager', 'staff']}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="organizations" replace />} />
            <Route path="organizations" element={<OrganizationsPage />} />
            <Route path="organizations/:id" element={<OrganizationDetailPage />} />
            <Route path="onboard" element={<OnboardClientPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="audit" element={<AuditLogPage />} />
            <Route path="templates" element={<FolderTemplatesPage />} />
            <Route path="request-templates" element={<RequestTemplatesPage />} />
            <Route path="settings" element={<SystemSettingsPage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <ToastRegion />
        <CommandPalette />
        <GlobalQuickUploadModal />
        <GuidedTour />
    </>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppInner />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
